import {
  booleanAttribute,
  Component,
  computed,
  input,
  model,
  numberAttribute,
  output,
  viewChild,
} from '@angular/core';

import { SafeHtml } from '@angular/platform-browser';
import { generateId } from '@lc/util';

export type TextareaSize = 'small' | 'medium' | 'large';

export const bspkTextareaDefaults = {
  size: 'medium',
  minRows: 3,
  maxRows: 10,
} as const;

const getMinRows = (value: unknown) => Math.max(bspkTextareaDefaults.minRows, numberAttribute(value));

const getMaxRows = (value: unknown) => Math.min(bspkTextareaDefaults.maxRows, numberAttribute(value));

@Component({
  selector: 'bspk-textarea',
  imports: [],
  templateUrl: './textarea.component.html',
  styleUrl: './textarea.component.scss',
  host: {
    '[attr.data-textarea]': 'true',
    '[attr.data-size]': 'size()',
    '[style]': "{ '--min-rows': minRows(), '--max-rows': maxRows() }",
  },
})
export class BspkTextarea {
  readonly ariaLabel = input.required({ alias: 'aria-label' });
  readonly value = model<string>('');
  readonly size = input<TextareaSize>(bspkTextareaDefaults.size);
  readonly errorMessage = input<string | SafeHtml>();
  readonly placeholder = input<string>('');
  readonly maxLength = input<number | null>(null); // Having this as -1 disables the maxlength
  readonly maxRows = input(bspkTextareaDefaults.maxRows, { transform: getMaxRows });
  readonly minRows = input(bspkTextareaDefaults.minRows, { transform: getMinRows });
  readonly id = input(generateId('textarea'));
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly invalid = input(false, { transform: booleanAttribute });
  readonly readOnly = input(false, { transform: booleanAttribute });
  readonly required = input(false, { transform: booleanAttribute });
  readonly ariaInvalid = computed(() => !this.readOnly() && !this.disabled() && this.invalid());
  readonly blur = output<Event>();

  readonly replicatedValue = viewChild.required<HTMLDivElement>('dataReplicatedValue');

  onBlur(event: Event) {
    const target = event.target as HTMLTextAreaElement;
    target.scrollTop = 0;
    this.blur.emit(event);
  }

  onInput(event: Event) {
    const target = event.target as HTMLTextAreaElement;

    this.value.set(target.value);

    // we know the textarea was resized, so we don't want to auto-size it
    if (target.style.height) return;
    this.replicatedValue().innerText = `${target.value}\n`;
  }
}
